import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { domainToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

export const contentstack = defineConnector({
  identifier: "contentstack",
  name: "Contentstack",
  tagLine: "Headless content management",
  logo,
  protocol: "http",
  tags: ["productivity"],
  defaultUrlPattern: ({ apiDomain }) => domainToRegex(apiDomain),
  auth: oauthAuth,
  steps: [
    {
      title: "Enter your Contentstack instance details",
      items: [
        {
          kind: "markdown",
          content: "Enter the details for your Contentstack instance.",
        },
        {
          kind: "input",
          label: "App ID",
          placeholder: "your-appId",
          required: true,
          target: { kind: "configuration", field: "appId" },
          helperText: "The app ID of your Contentstack account",
        },
        {
          kind: "input",
          label: "Contentstack Domain",
          placeholder: "domain",
          required: true,
          target: { kind: "configuration", field: "subdomain" },
          helperText:
            "The subdomain of your Contentstack account (e.g. https://domain.contentstack.com)",
        },
        {
          kind: "input",
          label: "API Domain",
          placeholder: "eu-api.contentstack.com",
          required: true,
          target: { kind: "configuration", field: "apiDomain" },
          helperText:
            "The domain to where you will access your API (e.g. https://eu-api.contentstack.com)",
        },
      ],
    },
    {
      title: "Create a Contentstack app",
      items: [
        {
          kind: "markdown",
          content:
            "Go to the [Contentstack Developer Hub](https://www.contentstack.com/docs/developers/developer-hub/) and create a new app with OAuth enabled to get your client ID and client secret.",
        },
      ],
    },
    {
      title: "Configure the redirect URI",
      items: [
        {
          kind: "markdown",
          content: "In your app’s settings, add the following redirect URI:",
        },
        {
          kind: "copyable",
          label: "Redirect URI",
          value: "{API_URL}/connected-apps/oauth/contentstack/callback",
        },
      ],
    },
    {
      title: "Enter your app credentials",
      items: [
        {
          kind: "input",
          label: "Client ID",
          placeholder: "Paste your Client ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          placeholder: "Paste your Client secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Authorize your Contentstack account",
      items: [
        {
          kind: "markdown",
          content: "Click below to authorize your Contentstack account.",
        },
        {
          kind: "oauth",
          dependsOn: [
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
          ],
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "authorization_code",
    authorizationEndpoint: (configuration) =>
      `https://${configuration.subdomain}.contentstack.com/apps/${configuration.appId}/authorize`,
    tokenEndpoint: (configuration) =>
      `https://${configuration.subdomain}.contentstack.com/apps-api/apps/token`,
    tokenEndpointAuthMethod: () => "client_secret_post",
    clientCredentialsFrom: "db",
    scopes: () => [],
  },
  connectionTest: {
    protocol: "http",
    request: (configuration) => `https://${configuration.apiDomain}/v3/user`,
  },
  publicEnvVars: ({ apiDomain, subdomain, appId }) => ({
    CONTENTSTACK_API_URL: apiDomain ? `https://${apiDomain}` : "",
    CONTENTSTACK_URL: subdomain ? `https://${subdomain}.contentstack.com` : "",
    CONTENTSTACK_APP_ID: appId || "",
  }),
});
